// Shipyard section — the ships a harbour city can lay down.
//
// Ships are ordinary units: `start_building` with a ship kind routes through the
// building queue, which spawns the hull on the water beside the harbour. The
// snapshot has a buildings catalog but no unit catalog yet, so the four hulls
// are listed here; the sim re-validates cost, prerequisites and the harbour.
import { Button } from '@aod/ui/components/ui/button';
import { Separator } from '@aod/ui/components/ui/separator';
import { type MessageKey, useT } from '../../i18n/index.js';
import { Row } from './InfoPanelShell.js';

interface HullDef {
  readonly id: string;
  readonly label: MessageKey;
  readonly cost: number;
  readonly turns: number;
  readonly requires?: string;
}

/** The four hulls in `content/units/roster_basic.rs`, in build order. */
const HULLS: ReadonlyArray<HullDef> = [
  { id: 'trade_ship', label: 'ship.trade', cost: 60, turns: 6 },
  { id: 'transport_ship', label: 'ship.transport', cost: 50, turns: 5 },
  { id: 'warship', label: 'ship.warship', cost: 100, turns: 12, requires: 'shipyard' },
  {
    id: 'galleon',
    label: 'ship.galleon',
    cost: 200,
    turns: 16,
    requires: 'grand_shipyard',
  },
];

export function ShipyardSection({
  cityId,
  completedBuildings,
  treasuryIndustry,
  onStartBuilding,
}: {
  cityId: string;
  completedBuildings: ReadonlyArray<string>;
  treasuryIndustry: number;
  onStartBuilding: (cityId: string, buildingId: string) => void;
}) {
  const t = useT();
  return (
    <>
      <Separator />
      <div className="flex flex-col gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t('ship.shipyard')}
        </span>
        {HULLS.map((hull) => {
          const locked = hull.requires !== undefined && !completedBuildings.includes(hull.requires);
          const poor = treasuryIndustry < hull.cost;
          return (
            <div key={hull.id} className="flex items-center justify-between gap-2">
              <div className="flex flex-col">
                <span className="text-[11px] text-foreground">{t(hull.label)}</span>
                <Row label={t('ship.cost')}>
                  {hull.cost} · {t('ship.turns', { turns: hull.turns })}
                </Row>
              </div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={locked || poor}
                title={
                  locked ? t('building.requires', { building: hull.requires ?? '' }) : undefined
                }
                onClick={() => onStartBuilding(cityId, hull.id)}
              >
                {locked ? t('ship.locked') : t('ship.build')}
              </Button>
            </div>
          );
        })}
      </div>
    </>
  );
}
